/**
 * Narration & Voiceover Coordinator for Lesson Narratives, Dialogue, and Speech Highlights.
 */
import { VoicePlayer, SpeechRequest } from './VoicePlayer';
import { SubtitleOverlay } from './Subtitle';
import { Character } from '../characters/Character';
import { Scene } from '../core/Scene';
import { AudioManager } from './AudioManager';

export interface NarrationItem {
  id: string;
  time: number;
  duration?: number;
  text: string;
  speakerId?: string;
  emotion?: string;
  soundCue?: 'chime' | 'pop' | 'ding' | 'whoosh' | 'correct' | 'wrong';
  rate?: number;
  pitch?: number;
  lang?: string;
  language?: string;
  showSubtitles?: boolean;
}

export class NarrationCoordinator {
  private voicePlayer: VoicePlayer;
  private audioManager: AudioManager;
  private subtitleOverlay?: SubtitleOverlay;
  private scene?: Scene;
  private currentPlayingId: string | null = null;

  constructor(voicePlayer: VoicePlayer, audioManager: AudioManager) {
    this.voicePlayer = voicePlayer;
    this.audioManager = audioManager;
  }

  public setSceneContext(scene: Scene, subtitleOverlay?: SubtitleOverlay): void {
    this.scene = scene;
    this.subtitleOverlay = subtitleOverlay;
  }

  public playNarration(item: NarrationItem & { src?: string; audioUrl?: string }): void {
    this.currentPlayingId = item.id;

    // 1. Play optional sound cue
    if (item.soundCue) {
      this.audioManager.playSoundCue(item.soundCue);
    }

    // 2. Identify speaker character if present (with fallback resolution)
    let speaker: Character | undefined;
    if (this.scene) {
      if (item.speakerId) {
        speaker = this.scene.getObject<Character>(item.speakerId);
      }
      if (!speaker) {
        const allChars = this.scene.getAllObjects().filter(o => o instanceof Character) as Character[];
        if (item.speakerId) {
          speaker = allChars.find(c => c.id.toLowerCase().includes(item.speakerId!.toLowerCase()));
        }
        if (!speaker && allChars.length > 0) {
          speaker = allChars[0];
        }
      }

      if (speaker instanceof Character) {
        speaker.setAction('talking');
        if (item.emotion) {
          speaker.setEmotion(item.emotion as any);
        }
        const speechDur = item.duration || Math.max(2.5, item.text.length * 0.08);
        speaker.speakText(item.text, speechDur);
      }
    }

    // 3. Show subtitles if enabled
    if (this.subtitleOverlay && (item.showSubtitles !== false)) {
      this.subtitleOverlay.setCaption(item.text);
    }

    // 4. Play Recorded Microphone Audio File if available, otherwise fallback to Web Speech TTS
    const audioUrl = item.src || item.audioUrl;
    if (audioUrl) {
      this.voicePlayer.stop();
      this.audioManager.playAudioTrack(audioUrl);
      return;
    }

    // 5. Trigger Web Speech Synthesis TTS
    const words = item.text.split(/\s+/);
    let wordCounter = 0;

    const charVoice = speaker?.props?.voice;
    const finalGender = charVoice?.gender || (speaker?.id?.includes('priya') || speaker?.id?.includes('student') ? 'female' : 'male');
    const finalRate = item.rate || charVoice?.rate || 1.0;
    const finalPitch = item.pitch || charVoice?.pitch || (finalGender === 'female' ? 1.25 : (finalGender === 'robot' ? 0.55 : 0.9));
    const finalVoiceName = charVoice?.voiceName;
    const targetLang = item.language || item.lang;

    this.voicePlayer.speak({
      text: item.text,
      gender: finalGender,
      voiceName: finalVoiceName,
      rate: finalRate,
      pitch: finalPitch,
      lang: targetLang,
      language: targetLang,
      onWord: (word, charIndex) => {
        if (this.subtitleOverlay) {
          this.subtitleOverlay.highlightWord(wordCounter);
          wordCounter++;
        }
      },
      onViseme: (viseme) => {
        if (speaker) {
          speaker.rig.setViseme(viseme);
        }
      },
      onEnd: () => {
        if (this.currentPlayingId === item.id) {
          if (speaker) {
            speaker.setAction('idle');
            speaker.rig.setViseme('rest');
          }
          if (this.subtitleOverlay) {
            // keep caption briefly or fade
            setTimeout(() => {
              if (this.currentPlayingId === item.id && this.subtitleOverlay) {
                this.subtitleOverlay.setCaption('');
              }
            }, 1200);
          }
          this.currentPlayingId = null;
        }
      }
    });
  }

  public stop(): void {
    this.voicePlayer.stop();
    this.currentPlayingId = null;
    if (this.subtitleOverlay) {
      this.subtitleOverlay.setCaption('');
    }
  }
}
